import { notifyGamificationRewardOutcomes } from "@app/gamification/show-reward-outcome";
import type { GamificationRewardOutcome } from "@core/gamification";
import { lessonAtom } from "@core/grades/atoms";
import type { Lesson } from "@core/grades/types";
import { parseError, URLS } from "@core/shared/utils";
import { useIsGuest } from "@framework/account/hooks";
import { useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { useNavigate } from "@remix-run/react";
import { useCallback, useState } from "react";
import { errorToast } from "../../design-system/components/toast";

type LessonLikeResponse = {
  liked?: boolean;
  likes?: number;
  reward?: {
    xp: number;
    gold: number;
  } | null;
  rewardOutcome?: GamificationRewardOutcome | null;
};

type LikeableLesson = Pick<Lesson, "id"> &
  Partial<Pick<Lesson, "isPaid" | "liked" | "likes">>;

export function useLessonLikeForLesson(lesson: LikeableLesson) {
  const isGuest = useIsGuest();
  const navigate = useNavigate();
  const toggleLikeAction = useAction<LessonLikeResponse>(
    URLS.lessons.like(lesson.id),
  );
  const [liked, setLiked] = useState(Boolean(lesson.liked));
  const [likes, setLikes] = useState(lesson.likes || 0);
  const [isPending, setIsPending] = useState(false);
  // Detail payloads always include the definitive purchase state. Some
  // lightweight lesson-card payloads omit it, so let the secured backend
  // endpoint make the final ownership decision instead of hiding the action.
  const canLike = lesson.isPaid !== false;

  const toggleLike = useCallback(async () => {
    if (isGuest) {
      navigate("/auth/login");
      return;
    }

    if (!canLike) {
      errorToast(trans("grades.purchaseLessonToLike"));
      return;
    }

    if (isPending) return;

    setIsPending(true);

    const response = await toggleLikeAction({});

    setIsPending(false);

    if (!response.isOK) {
      errorToast(
        response.status === 403
          ? trans("ratings.notIncluded")
          : response.status === 400
            ? trans("grades.purchaseLessonToLike")
            : parseError(response.error) || trans("error.somethingWentWrong"),
      );
      return;
    }

    if (typeof response.data.liked === "boolean") {
      setLiked(response.data.liked);
    }

    if (typeof response.data.likes === "number") {
      setLikes(response.data.likes);
    }

    notifyGamificationRewardOutcomes(response.data.rewardOutcome);
  }, [canLike, isGuest, isPending, navigate, toggleLikeAction]);

  return {
    canLike,
    isPending,
    liked,
    likes,
    toggleLike,
  };
}

export function useLessonLike() {
  return useLessonLikeForLesson(lessonAtom.useValue());
}
